import type { Socket } from 'socket.io-client';
import socketService from '../../services/socketService';
import { replayLifecycleEvents } from './api';
import { ConversationLifecycleState, initialLifecycleState, reduceLifecycleEvents } from './reducer';
import { parseLifecycleEvent } from './types';

type StateListener = (state: ConversationLifecycleState) => void;

const MAX_REPLAY_PAGES = 20;

export class ConversationLifecycleController {
  private state = initialLifecycleState();
  private sessionId: string | null = null;
  private workerId: string | null = null;
  private socket: Socket | null = null;
  private listeners = new Set<StateListener>();
  private generation = 0;
  private reconcileTask: Promise<void> | null = null;
  private reconcileRequested = false;

  private readonly onSocketEvent = (value: unknown) => {
    if (!this.sessionId || !this.workerId) return;
    const event = parseLifecycleEvent(value, this.workerId, this.sessionId);
    if (!event) return;
    this.update(reduceLifecycleEvents(this.state, [event]));
    if (this.state.gapDetected) this.requestReconcile();
  };

  private readonly onSocketConnect = () => {
    this.requestReconcile();
  };

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  async start(sessionId: string, workerId: string): Promise<void> {
    this.stop();
    this.sessionId = sessionId;
    this.workerId = workerId;
    this.state = {
      ...initialLifecycleState(sessionId),
      cursor: socketService.getRememberedCursor(sessionId),
    };
    this.generation += 1;
    this.socket = socketService.connect();
    this.socket.on('connect', this.onSocketConnect);
    socketService.onConversationLifecycle(this.onSocketEvent);
    socketService.setActiveSession(workerId, sessionId);
    this.notify();
    await this.reconcile();
  }

  stop(): void {
    this.generation += 1;
    this.socket?.off('connect', this.onSocketConnect);
    socketService.offConversationLifecycle(this.onSocketEvent);
    if (this.workerId) socketService.clearActiveSession(this.workerId, this.sessionId ?? undefined);
    this.socket = null;
    this.workerId = null;
    this.sessionId = null;
    this.reconcileTask = null;
    this.reconcileRequested = false;
    this.state = initialLifecycleState();
  }

  getSnapshot(): ConversationLifecycleState {
    return this.state;
  }

  async reconcile(): Promise<void> {
    if (!this.sessionId || !this.workerId) return;
    if (this.reconcileTask) {
      this.reconcileRequested = true;
      return this.reconcileTask;
    }

    const task = (async () => {
      do {
        this.reconcileRequested = false;
        await this.reconcileOnce();
      } while (this.reconcileRequested && this.sessionId && this.workerId);
    })();
    this.reconcileTask = task;

    try {
      await task;
    } finally {
      if (this.reconcileTask === task) this.reconcileTask = null;
    }
  }

  private requestReconcile(): void {
    void this.reconcile().catch(() => {
      if (this.sessionId) this.update({ ...this.state, synchronized: false });
    });
  }

  private async reconcileOnce(): Promise<void> {
    if (!this.sessionId || !this.workerId) return;
    const generation = this.generation;
    const sessionId = this.sessionId;
    const workerId = this.workerId;
    // A persisted cursor is only a resume hint; it cannot hydrate a new reducer instance.
    let fullReplay = this.state.gapDetected || Object.keys(this.state.byEventId).length === 0;
    let merged = fullReplay
      ? initialLifecycleState(sessionId)
      : { ...this.state, gapDetected: false, synchronized: false };

    for (let pageNumber = 0; pageNumber < MAX_REPLAY_PAGES; pageNumber += 1) {
      const page = await replayLifecycleEvents(sessionId, workerId, merged.cursor);
      if (generation !== this.generation || sessionId !== this.sessionId || workerId !== this.workerId) return;

      if (page.cursorExpired) {
        if (fullReplay) {
          this.update({ ...merged, synchronized: false, gapDetected: true });
          return;
        }
        fullReplay = true;
        merged = initialLifecycleState(sessionId);
        pageNumber = -1;
        continue;
      }

      merged = reduceLifecycleEvents(merged, page.events);
      if (merged.gapDetected) {
        if (fullReplay) {
          this.update({ ...merged, synchronized: false });
          return;
        }
        fullReplay = true;
        merged = initialLifecycleState(sessionId);
        pageNumber = -1;
        continue;
      }

      if (!page.hasMore) {
        merged = reduceLifecycleEvents(merged, Object.values(this.state.byEventId));
        if (merged.gapDetected) {
          if (!fullReplay) {
            fullReplay = true;
            merged = initialLifecycleState(sessionId);
            pageNumber = -1;
            continue;
          }
          this.update({ ...merged, synchronized: false });
          return;
        }
        this.update({ ...merged, synchronized: page.synchronized, gapDetected: false });
        return;
      }
    }

    this.update({ ...merged, synchronized: false });
  }

  private update(state: ConversationLifecycleState): void {
    this.state = state;
    if (this.sessionId) socketService.rememberCursor(this.sessionId, state.cursor);
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.state));
  }
}
